import React, { useState, useEffect, useContext, lazy, Suspense } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Book, User, ArrowLeft, Edit, Plus, Trash2, 
  School, BookOpen, Calendar, Loader, AlertCircle,
  AlertTriangle, Users, Filter, FileText, Download,
  ThumbsUp, ThumbsDown, Flag, Eye, ArrowUpDown,
  MessageSquare, Upload, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';
import QuestionsTab from '../../components/questions/QuestionsTab';
import ComplianceMaterialUpload from '../../components/ComplianceMaterialUpload';

// Lazy-load DocumentViewer to improve initial page load performance
const DocumentViewer = lazy(() => import('../../components/DocumentViewer'));

/**
 * Course Details Page component
 * Shows course information, sections, and materials
 */
const CourseDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [course, setCourse] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [commentCounts, setCommentCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Material filters
  const [typeFilter, setTypeFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  // Upload modal and viewer states
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentMaterial, setCurrentMaterial] = useState(null);

  // Material upload state
  const [uploading, setUploading] = useState(false);

  // Request section states
  const [sectionRequestModalOpen, setSectionRequestModalOpen] = useState(false);
  const [requestingNewProfessor, setRequestingNewProfessor] = useState(false);
  const [professorsList, setProfessorsList] = useState([]);
  const [sectionRequestLoading, setSectionRequestLoading] = useState(false);
  const [sectionRequest, setSectionRequest] = useState({
    semester: '',
    year: new Date().getFullYear(),
    professorId: '',
    newProfessorName: ''
  });

  // Fetch course and materials data
  useEffect(() => {
    const fetchCourseDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch course details
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/courses/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch course details');
        }
        
        const data = await response.json();
        setCourse(data);
        
        // Fetch all materials for this course
        const materialsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/courses/${id}/materials`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (materialsResponse.ok) {
          const materialsData = await materialsResponse.json();
          setMaterials(materialsData);
          
          // Fetch comment counts for materials
          if (materialsData && materialsData.length > 0) {
            fetchCommentCounts(materialsData.map(m => m.id));
          }
        }
      } catch (error) {
        console.error('Error fetching course details:', error);
        setError(error.message);
        toast.error('Failed to load course details');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchCourseDetails();
    }
  }, [id]);

  // Fetch professors for request modal
  useEffect(() => {
    const fetchProfessors = async () => {
      if (!user?.schoolId || !sectionRequestModalOpen) return;
      
      const schoolId = user.schoolId || user.school?.id;
      if (!schoolId) return;
      
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/professors`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (res.ok) {
          const data = await res.json();
          setProfessorsList(data);
        } else {
          console.error('Failed to fetch professors:', res.status);
        }
      } catch (error) {
        console.error('Error fetching professors:', error);
      }
    };
    
    fetchProfessors();
  }, [user, sectionRequestModalOpen]);

  const handleSectionRequestSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSectionRequestLoading(true);
      
      // Validate input
      if (!sectionRequest.semester) {
        toast.error('Please select a semester');
        return;
      }
      
      if (requestingNewProfessor && !sectionRequest.newProfessorName) {
        toast.error('Please provide a professor name');
        return;
      }
      
      // Construct request data
      const requestData = {
        courseId: id,
        semester: sectionRequest.semester,
        year: sectionRequest.year,
        professorId: requestingNewProfessor ? null : sectionRequest.professorId,
        newProfessorName: requestingNewProfessor ? sectionRequest.newProfessorName : null
      };
      
      // Send request to API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/section-requests`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit section request');
      }
      
      // Close modal and show success message
      setSectionRequestModalOpen(false);
      toast.success('Section request submitted successfully! It will be reviewed by moderators.');
      
      // Reset form
      setSectionRequest({
        semester: '',
        year: new Date().getFullYear(),
        professorId: '',
        newProfessorName: ''
      });
      setRequestingNewProfessor(false);
    } catch (error) {
      console.error('Error submitting section request:', error);
      toast.error(error.message || 'Failed to submit section request. Please try again.');
    } finally {
      setSectionRequestLoading(false);
    }
  };

  // Fetch comment counts for materials
  const fetchCommentCounts = async (materialIds) => {
    if (!materialIds || materialIds.length === 0) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/comments/counts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ materialIds })
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch comment counts');
      }
      
      const counts = await response.json();
      setCommentCounts(counts);
    } catch (error) {
      console.error('Error fetching comment counts:', error);
    }
  };
  
  const handleDeleteSection = async (sectionId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/course-sections/${sectionId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete section');
      }
      
      // Update course data
      setCourse({
        ...course,
        sections: course.sections.filter(section => section.id !== sectionId)
      });
      
      setDeleteConfirmation(null);
      toast.success('Course section deleted successfully');
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error(error.message || 'Failed to delete section');
    }
  };

  // Function to handle authenticated downloads
  const handleDownload = async (material) => {
    try {
      toast.loading('Preparing download...');
      
      // Process the URL to make sure it's properly formatted
      let apiUrl;
      const fileUrl = material.fileUrl;
      
      // If it's already an API URL, use it directly
      if (fileUrl.startsWith('/api/files/')) {
        apiUrl = `${process.env.REACT_APP_API_URL}${fileUrl}`;
      } 
      // Otherwise, construct the proper path
      else {
        // Extract the file path - this may need adjustment based on your URL structure
        let filePath;
        if (fileUrl.includes('api.edx.red')) {
          try {
            const urlObj = new URL(fileUrl);
            filePath = urlObj.pathname.replace(/^\/api\/files\//, '');
          } catch (e) {
            console.error("Error parsing URL:", e);
            filePath = fileUrl.split('/').pop();
          }
        } else {
          filePath = fileUrl;
        }
        
        apiUrl = `${process.env.REACT_APP_API_URL}/api/files/${filePath}`;
      }
      
      // Add download parameter
      apiUrl += '?download=true';
      
      // Fetch the file with authentication
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      // Get the file data as a blob
      const blob = await response.blob();
      
      // Create a URL for the blob
      const downloadUrl = URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = material.title || material.fileUrl.split('/').pop();
      
      // Append to the document
      document.body.appendChild(a);
      
      // Click the link
      a.click();
      
      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      
      toast.dismiss();
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.dismiss();
      toast.error(`Download failed: ${error.message}`);
    }
  };

  // View material details
  const handleViewMaterial = (material) => {
    // Track the view
    trackMaterialView(material.id);
    
    // Set the current material and open the viewer
    setCurrentMaterial(material);
    setViewerOpen(true);
  };

  // Close the document viewer
  const handleCloseViewer = () => {
    setViewerOpen(false);
    setCurrentMaterial(null);
    
    // Refresh comment counts after viewing
    if (materials && materials.length > 0) {
      fetchCommentCounts(materials.map(m => m.id));
    }
  };

  // Track material view
  const trackMaterialView = async (materialId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/${materialId}/view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        console.log(`Successfully tracked view for material ${materialId}`);
      } else {
        console.error('Failed to track material view:', await response.text());
      }
    } catch (error) {
      console.error('Error tracking material view:', error);
    }
  };

  // Vote on a material
  const handleVote = async (materialId, voteType) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/${materialId}/vote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ voteType })
      });
      
      if (!response.ok) {
        throw new Error('Failed to vote');
      }
      
      const updatedVote = await response.json();
      
      // Update materials state with new vote counts
      setMaterials(materials.map(material => 
        material.id === materialId 
          ? { ...material, upvotes: updatedVote.upvotes, downvotes: updatedVote.downvotes } 
          : material
      ));
      
      toast.success(`${voteType === 'upvote' ? 'Upvoted' : 'Downvoted'} material`);
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to vote on material');
    }
  };

  // Report a material
  const handleReport = async (materialId) => {
    const reason = prompt('Please provide a reason for reporting this material:');
    if (!reason || !reason.trim()) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/${materialId}/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: reason.trim() })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to report material');
      }
      
      toast.success('Material reported successfully');
    } catch (error) {
      console.error('Error reporting material:', error);
      toast.error(error.message || 'Failed to report material');
    }
  };

  // Delete material
  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this material? This action cannot be undone.')) {
      return;
    }
    
    // Show loading state and optimistically remove from UI
    toast.loading('Deleting material...', { id: `delete-${materialId}` });
    const materialToDelete = materials.find(m => m.id === materialId);
    setMaterials(materials.filter(material => material.id !== materialId));
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials/${materialId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        // If delete failed, restore the material to the list
        setMaterials(prevMaterials => [...prevMaterials, materialToDelete].sort((a, b) => b.createdAt - a.createdAt));
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete material');
      }
      
      toast.success('Material deleted successfully', { id: `delete-${materialId}` });
    } catch (error) {
      console.error('Error deleting material:', error);
      toast.error(error.message || 'Failed to delete material', { id: `delete-${materialId}` });
    }
  };

  // Upload material
  const handleUploadSubmit = async (materialData) => {
    if (!materialData.file || !materialData.title || !materialData.type) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', materialData.file);
      formData.append('title', materialData.title);
      formData.append('description', materialData.description || '');
      formData.append('type', materialData.type);
      formData.append('courseId', id);
      // Note: CourseDetailsPage uploads go to the course level, not a specific section
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/materials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle compliance validation errors specially
        if (response.status === 422) {
          toast.error(`Content Compliance Error: ${errorData.message}`);
          if (errorData.details && errorData.details.length > 0) {
            errorData.details.forEach(detail => {
              toast.error(detail.message || detail, { duration: 6000 });
            });
          }
        } else {
          throw new Error(errorData.error || 'Failed to upload material');
        }
        return;
      }
      
      const newMaterial = await response.json();
      
      // Add new material to state
      setMaterials([newMaterial, ...materials]);
      setUploadModalOpen(false);
      
      toast.success('Material uploaded successfully!');
      
      // Show additional message if under review
      if (newMaterial.complianceStatus === 'under_review') {
        toast.info('Your material is under review and will be published after approval.', { duration: 5000 });
      }
      
    } catch (error) {
      console.error('Error uploading material:', error);
      toast.error('Failed to upload material: ' + error.message);
    } finally {
      setUploading(false);
    }
  };
  
  // Filter materials based on current filters
  const filteredMaterials = materials
    .filter(material => !typeFilter || material.type === typeFilter)
    .sort((a, b) => {
      if (sortOrder === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortOrder === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortOrder === 'most-upvoted') return b.upvotes - a.upvotes;
      return 0;
    });
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader className="h-8 w-8 text-red-500 animate-spin mr-2" />
        <span className="text-gray-600 dark:text-gray-400">Loading course details...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Error Loading Course
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          {error}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Go Back
        </button>
      </div>
    );
  }
  
  if (!course) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Course Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          The course you're looking for couldn't be found or you don't have access to it.
        </p>
        <Link
          to="/courses"
          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <ArrowLeft className="mr-2 -ml-1 h-5 w-5" />
          Back to Courses
        </Link>
      </div>
    );
  }
  
  const isAdmin = user && (user.role === 'admin' || user.role === 'moderator');

  
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Navigation breadcrumbs */}
      <div className="flex items-center mb-6 text-sm">
        <Link to="/courses" className="text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400">
          Courses
        </Link>
        <span className="mx-2 text-gray-400 dark:text-gray-600">/</span>
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          {course.code}
        </span>
      </div>
      
      {/* Course Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center mb-2">
              <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mr-3">
                <Book size={24} />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {course.code}: {course.name}
              </h1>
            </div>
            <div className="flex items-center text-gray-700 dark:text-gray-300 mb-3 ml-14">
              <School className="h-4 w-4 mr-1" />
              <span>{course.schoolName || 'Unknown School'}</span>
            </div>
            <div className="flex items-center text-gray-700 dark:text-gray-300 mb-4 ml-14">
              <BookOpen className="h-4 w-4 mr-1" />
              <span>{course.departmentName || 'Unknown Department'} ({course.departmentCode || 'N/A'})</span>
            </div>
            {course.description && (
              <p className="text-gray-600 dark:text-gray-400 mt-4 ml-14">
                {course.description}
              </p>
            )}
          </div>
          
          <div className="mt-4 md:mt-0 flex flex-col space-y-2">
            {isAdmin && (
              <Link
                to={`/courses/${course.id}/edit`}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Edit className="mr-2 -ml-1 h-5 w-5" />
                Edit Course
              </Link>
            )}
            <button
              onClick={() => setUploadModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <Upload className="mr-2 -ml-1 h-5 w-5" />
              Upload Material
            </button>
            {isAdmin && (
              <Link
                to={`/courses/${course.id}/sections/new`}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <Plus className="mr-2 -ml-1 h-5 w-5" />
                Add Section
              </Link>
            )}
            {user && user.role === 'student' && (
              <button
                onClick={() => setSectionRequestModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="mr-2 -ml-1 h-5 w-5" />
                Request Section
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Tabs Navigation */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'overview'
              ? 'border-b-2 border-red-500 text-red-600 dark:text-red-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('materials')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'materials'
              ? 'border-b-2 border-red-500 text-red-600 dark:text-red-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Materials ({materials.length})
        </button>
        <button
          onClick={() => setActiveTab('sections')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'sections'
              ? 'border-b-2 border-red-500 text-red-600 dark:text-red-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Sections ({course.sections?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('questions')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'questions'
              ? 'border-b-2 border-red-500 text-red-600 dark:text-red-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Questions
        </button>
      </div>
      
      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Course Details */}
          <div className="md:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              About This Course
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {course.description || 'No description available for this course.'}
            </p>
            
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
              Course Information
            </h3>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 text-red-500 mr-2">
                  <BookOpen />
                </div>
                <span className="text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Department:</span> {course.departmentName} ({course.departmentCode})
                </span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 text-red-500 mr-2">
                  <School />
                </div>
                <span className="text-gray-600 dark:text-gray-400">
                  <span className="font-medium">School:</span> {course.schoolName}
                </span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 text-red-500 mr-2">
                  <FileText />
                </div>
                <span className="text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Materials:</span> {materials.length} available
                </span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 text-red-500 mr-2">
                  <Users />
                </div>
                <span className="text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Sections:</span> {course.sections?.length || 0} available
                </span>
              </li>
            </ul>
          </div>
          
          {/* Recent Materials Sidebar */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Recent Materials
              </h2>
              <button
                onClick={() => setActiveTab('materials')}
                className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
              >
                View All
              </button>
            </div>
            
            {materials.length > 0 ? (
              <div className="space-y-4">
                {materials.slice(0, 5).map((material) => (
                  <div key={material.id} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-0">
                    <div className="flex items-start">
                      <div className={`p-2 rounded-md mr-2 flex-shrink-0 ${
                        material.type === 'notes'
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300'
                          : material.type === 'exam'
                          ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300'
                          : material.type === 'syllabus'
                          ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}>
                        <FileText size={16} />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white hover:text-red-600 dark:hover:text-red-400 cursor-pointer" onClick={() => handleViewMaterial(material)}>
                          {material.title}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {material.sectionId 
                            ? `${material.semester} ${material.year} - Prof. ${material.professorName}`
                            : 'General Course Material'}
                        </p>
                        <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <span>By <Link 
                          to={`/users/${material.userId}`}
                          className="hover:text-red-600 dark:hover:text-red-400"
                        >
                          {material.userName}
                        </Link></span>
                          <span>{new Date(material.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <FileText className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-gray-500 dark:text-gray-400">No materials yet</p>
                <button
                  onClick={() => setUploadModalOpen(true)}
                  className="mt-3 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
                >
                  Upload the first material
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Materials Tab */}
      {activeTab === 'materials' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Course Materials
            </h2>
            
            {/* Materials action buttons */}
            <div className="mt-4 md:mt-0 flex space-x-2">
              <button
                onClick={() => setUploadModalOpen(true)}
                className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <Upload className="mr-1 -ml-1 h-4 w-4" />
                Upload
              </button>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Filter className="mr-1 -ml-1 h-4 w-4" />
                Filter
              </button>
            </div>
          </div>
          
          {/* Filters */}
          {showFilters && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Material Type
                  </label>
                  <select
                    id="type-filter"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">All Types</option>
                    <option value="notes">Notes</option>
                    <option value="exam">Exams</option>
                    <option value="syllabus">Syllabi</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="sort-order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Sort By
                  </label>
                  <select
                    id="sort-order"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="most-upvoted">Most Upvoted</option>
                  </select>
                </div>
              </div>
            </div>
          )}
          
          {/* Materials List */}
          {filteredMaterials.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Materials Yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                {typeFilter 
                  ? `No materials of type "${typeFilter}" found for this course.` 
                  : "This course doesn't have any materials uploaded yet."
                }
              </p>
              <button
                onClick={() => setUploadModalOpen(true)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Upload className="mr-2 -ml-1 h-5 w-5" />
                Upload Material
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredMaterials.map((material) => (
                <div
                  key={material.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-red-500 dark:hover:border-red-500 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-start">
                    <div className="flex-1">
                      <div className="flex items-start">
                        <div
                          className={`p-2 rounded-md mr-3 ${
                            material.type === 'notes'
                              ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300'
                              : material.type === 'exam'
                              ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300'
                              : material.type === 'syllabus'
                              ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          <FileText size={20} />
                        </div>
                        
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                            {material.title}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Uploaded by <Link 
                              to={`/users/${material.userId}`}
                              className="hover:text-red-600 dark:hover:text-red-400"
                            >
                              {material.userName}
                            </Link>
                          </p>
                          {material.sectionId && (
                            <p className="text-sm text-blue-600 dark:text-blue-400">
                              {material.semester} {material.year} - Prof. {material.professorName}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            {new Date(material.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      {material.description && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 ml-12">
                          {material.description}
                        </p>
                      )}
                    </div>
                    
                    <div className="mt-4 md:mt-0 md:ml-4 flex space-x-2">
                      {/* View button */}
                      <button
                        onClick={() => handleViewMaterial(material)}
                        className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Eye className="mr-1 -ml-1 h-4 w-4" />
                        View
                      </button>
                      
                      {/* Download button */}
                      <button
                        onClick={() => handleDownload(material)}
                        className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <Download className="mr-1 -ml-1 h-4 w-4" />
                        Download
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handleVote(material.id, 'upvote')}
                        className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                      >
                        <ThumbsUp className="h-4 w-4 mr-1" />
                        <span>{material.upvotes}</span>
                      </button>
                      
                      <button
                        onClick={() => handleVote(material.id, 'downvote')}
                        className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <ThumbsDown className="h-4 w-4 mr-1" />
                        <span>{material.downvotes}</span>
                      </button>
                      
                      <button
                        onClick={() => handleViewMaterial(material)}
                        className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                      >
                        <MessageSquare className="h-4 w-4 mr-1" />
                        <span>{commentCounts[material.id] || 0}</span>
                      </button>
                      
                      <button
                        onClick={() => handleReport(material.id)}
                        className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400"
                      >
                        <Flag className="h-4 w-4 mr-1" />
                        <span className="text-sm">Report</span>
                      </button>
                      
                      {/* Delete button - only show for material owner or moderators/admins */}
                      {(user && (material.userName === user.name || user.role === 'moderator' || user.role === 'admin')) && (
                        <button
                          onClick={() => handleDeleteMaterial(material.id)}
                          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          <span className="text-sm">Delete</span>
                        </button>
                      )}
                    </div>
                    
                    <div className="text-xs text-gray-500 dark:text-gray-500">
                      {material.fileType} • {Math.round(material.fileSize / 1024)} KB
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Sections Tab */}
      {activeTab === 'sections' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Course Sections
          </h2>
          
          {course.sections && course.sections.length > 0 ? (
            <div className="space-y-4">
              {course.sections.map((section) => (
                <div 
                  key={section.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-red-500 dark:hover:border-red-500 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center">
                      <div className="p-2 rounded-md bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 mr-3">
                        <Calendar className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {section.semester} {section.year}
                        </h3>
                        <div className="flex items-center text-gray-600 dark:text-gray-400 mt-1">
                          <User className="h-4 w-4 mr-1" />
                          <span>Professor: {section.professorName}</span>
                        </div>
                        {section.sectionMaterialCount > 0 && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            <FileText className="h-4 w-4 inline mr-1" />
                            {section.sectionMaterialCount} materials
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-4 md:mt-0 flex space-x-2">
                      <Link
                        to={`/courses/${course.id}/sections/${section.id}`}
                        className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <Users className="mr-1 -ml-1 h-4 w-4" />
                        View
                      </Link>
                      
                      {isAdmin && (
                        <>
                          <Link
                            to={`/courses/${course.id}/sections/${section.id}/edit`}
                            className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <Edit className="mr-1 -ml-1 h-4 w-4" />
                            Edit
                          </Link>
                          
                          <button
                            onClick={() => setDeleteConfirmation(section)}
                            className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <Trash2 className="mr-1 -ml-1 h-4 w-4" />
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Sections Yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                This course doesn't have any sections yet.
              </p>
              {isAdmin && (
                <Link
                  to={`/courses/${course.id}/sections/new`}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <Plus className="mr-2 -ml-1 h-5 w-5" />
                  Add Section
                </Link>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Questions Tab */}
      {activeTab === 'questions' && (
        <QuestionsTab 
          courseId={id} 
          courseCode={course.code} 
          courseName={course.name}
          sections={course.sections || []}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center text-red-600 dark:text-red-400 mb-4">
              <AlertTriangle className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-medium">Confirm Deletion</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the section <strong>{deleteConfirmation.semester} {deleteConfirmation.year}</strong> taught by Professor {deleteConfirmation.professorName}? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSection(deleteConfirmation.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Material Modal */}
      <ComplianceMaterialUpload
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleUploadSubmit}
        uploading={uploading}
      />

      {/* Document viewer modal */}
      {viewerOpen && currentMaterial && (
        <Suspense fallback={<div className="fixed inset-0 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-gray-300 border-t-red-500 rounded-full animate-spin"></div>
        </div>}>
          <DocumentViewer
            fileUrl={currentMaterial.fileUrl}
            fileType={currentMaterial.fileType}
            title={currentMaterial.title}
            token={localStorage.getItem('token')}
            materialId={currentMaterial.id}
            currentUser={user}
            onClose={handleCloseViewer}
            sidebarWidth={256}
          />
        </Suspense>
      )}

      {/* Section Request Modal */}
      {sectionRequestModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => !sectionRequestLoading && setSectionRequestModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  onClick={() => !sectionRequestLoading && setSectionRequestModalOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Request New Section for {course.code}: {course.name}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Fill out this form to request a new section for this course. Your request will be reviewed by moderators.
                    </p>
                  </div>
                  
                  <div className="mt-4">
                    <form onSubmit={handleSectionRequestSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="section-term" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Section Term
                        </label>
                        <select
                          id="section-term"
                          value={sectionRequest.semester}
                          onChange={(e) => setSectionRequest({
                            ...sectionRequest,
                            semester: e.target.value
                          })}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          required
                        >
                          <option value="">Select Term</option>
                          <option value="Fall">Fall</option>
                          <option value="Winter">Winter</option>
                          <option value="Spring">Spring</option>
                          <option value="Summer">Summer</option>
                        </select>
                      </div>
                      
                      <div>
                        <label htmlFor="section-year" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Section Year
                        </label>
                        <input
                          type="number"
                          id="section-year"
                          value={sectionRequest.year}
                          onChange={(e) => setSectionRequest({
                            ...sectionRequest,
                            year: parseInt(e.target.value, 10)
                          })}
                          min={new Date().getFullYear()}
                          max={new Date().getFullYear() + 5}
                          className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          required
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="professor" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Professor
                        </label>
                        <select
                          id="professor"
                          value={requestingNewProfessor ? 'new' : sectionRequest.professorId}
                          onChange={(e) => {
                            if (e.target.value === 'new') {
                              setRequestingNewProfessor(true);
                              setSectionRequest({
                                ...sectionRequest,
                                professorId: ''
                              });
                            } else {
                              setRequestingNewProfessor(false);
                              setSectionRequest({
                                ...sectionRequest,
                                professorId: e.target.value
                              });
                            }
                          }}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          required
                        >
                          <option value="">Select Professor</option>
                          {professorsList.map((professor) => (
                            <option key={professor.id} value={professor.id}>
                              {professor.name}
                            </option>
                          ))}
                          <option value="new">Request New Professor</option>
                        </select>
                      </div>
                      
                      {/* New Professor Field (if requesting) */}
                      {requestingNewProfessor && (
                        <div>
                          <label htmlFor="new-professor-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Professor Name
                          </label>
                          <input
                            type="text"
                            id="new-professor-name"
                            value={sectionRequest.newProfessorName}
                            onChange={(e) => setSectionRequest({
                              ...sectionRequest,
                              newProfessorName: e.target.value
                            })}
                            className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            required={requestingNewProfessor}
                          />
                        </div>
                      )}
                      
                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={sectionRequestLoading}
                          className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${
                            sectionRequestLoading ? 'opacity-70 cursor-not-allowed' : ''
                          }`}
                        >
                          {sectionRequestLoading ? (
                            <>
                              <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                              Submitting...
                            </>
                          ) : 'Submit Request'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setSectionRequestModalOpen(false)}
                          disabled={sectionRequestLoading}
                          className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:w-auto sm:text-sm ${
                            sectionRequestLoading ? 'opacity-70 cursor-not-allowed' : ''
                          }`}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetailsPage;